import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Calendar, MapPin, BookOpen, Tent, Heart, Instagram, Facebook, Sparkles } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, MaskLine, fmtDate, fmtTime, fmtDay } from "../../lib/motion.jsx";
import { getActivities, getSocial } from "../../lib/api";
import { TikTokEmbed } from "../../components/TikTokEmbed";

const HERO = "https://customer-assets-0z36b82j.emergentagent.net/job_fc-inkad-preview/artifacts/iofv9w2z_WhatsApp%20Image%202026-08-28%20at%2020.38.46.webp";

const spring = { type: "spring", stiffness: 200, damping: 18 };

export default function JeugdHome() {
  const { t, lang } = useLang();
  const [acts, setActs] = useState([]);
  const [social, setSocial] = useState([]);

  useEffect(() => {
    getActivities("jeugd").then(setActs);
    getSocial("jeugd").then(setSocial);
  }, []);

  const cards = [
    { icon: Calendar, k: "sundayActivities", to: "/jeugd/activiteiten", color: "#E07A5F", desc: "Elke zondag · 17:00 - 18:30" },
    { icon: BookOpen, k: "homeworkClass", to: "/jeugd/huiswerkklas", color: "#81B29A", desc: "Wekelijkse begeleiding" },
    { icon: Tent, k: "holidayCamps", to: "/jeugd/vakantiekampen", color: "#F2CC8F", desc: "Avontuur in de vakantie" },
    { icon: Heart, k: "charity", to: "/jeugd/liefdadigheid", color: "#E07A5F", desc: "Samen voor de gemeenschap" },
  ];

  return (
    <div className="jeugd-theme" data-testid="jeugd-home">
      {/* HERO */}
      <section className="relative overflow-hidden pt-8 pb-4">
        <div className="mx-auto max-w-7xl px-6 grid gap-8 lg:grid-cols-2 items-center">
          <div className="pt-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#E07A5F]/10 px-4 py-1.5 text-xs font-semibold text-[#E07A5F] mb-5">
              <Sparkles size={13} /> {t("jHeroKicker")}
            </span>
            <h1 className="font-fredoka text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] text-[#262626]">
              <MaskLine>{t("jHero1")}</MaskLine>
              <MaskLine delay={0.12} className="text-[#E07A5F]">{t("jHero2")}</MaskLine>
              <MaskLine delay={0.24}>{t("jHero3")}</MaskLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="mt-6 max-w-md text-lg text-[#52525B] leading-relaxed">{t("jHeroLead")}</motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-8 flex flex-wrap gap-3">
              <Link to="/jeugd/inschrijven" className="rounded-full bg-[#E07A5F] px-7 py-3.5 font-bold text-white hover:scale-105 transition-transform duration-200">{t("joinJeugd")}</Link>
              <Link to="/jeugd/activiteiten" className="rounded-full border-2 border-[#262626]/10 px-7 py-3.5 font-bold text-[#262626] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors">{t("nav_activiteiten")}</Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ...spring }} className="relative">
            <div className="overflow-hidden rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
              <img src={HERO} alt="Inkad Jeugd" className="aspect-[4/5] w-full object-cover" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Editorial marquee */}
      <div className="py-8 border-y border-black/5 mt-8">
        <Marquee speed={35} gradient={false}>
          {["spelen", "leren", "kamperen", "delen", "groeien", "vrienden maken"].map((w, i) => (
            <span key={i} className="font-fredoka text-3xl font-medium text-[#262626]/70 mx-12">{w} <span className="text-[#E07A5F]">•</span></span>
          ))}
        </Marquee>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-16 space-y-24">
        {/* What we do */}
        <section>
          <Reveal><h2 className="font-fredoka text-4xl font-bold text-center mb-3">{t("whatWeDo")}</h2></Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mt-10">
            {cards.map((c, i) => (
              <Reveal key={c.k} delay={i * 0.08}>
                <Link to={c.to} data-testid={`jeugd-card-${c.k}`}
                  className="group block h-full rounded-3xl bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300">
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl transition-transform group-hover:scale-110" style={{ backgroundColor: c.color + "22" }}>
                    <c.icon size={26} style={{ color: c.color }} />
                  </div>
                  <h3 className="font-fredoka text-xl font-bold text-[#262626]">{t(c.k)}</h3>
                  <p className="mt-2 text-sm text-[#52525B]">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#E07A5F]">{t("viewAll")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Recent activities photos */}
        <section>
          <Reveal><h2 className="font-fredoka text-4xl font-bold mb-8">{t("recentActivities")}</h2></Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {acts.slice(0, 3).map((a, i) => (
              <Reveal key={a.id} delay={i * 0.08}>
                <div className="group overflow-hidden rounded-3xl bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                  <div className="aspect-[4/3] overflow-hidden"><img src={a.image} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" /></div>
                  <div className="p-6">
                    <div className="text-xs font-semibold text-[#81B29A] mb-1">{fmtDate(a.datetime, lang)}</div>
                    <h3 className="font-fredoka text-lg font-bold text-[#262626]">{a.title}</h3>
                    <p className="mt-2 text-sm text-[#52525B]">{a.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Social */}
        <section>
          <Reveal><h2 className="font-fredoka text-4xl font-bold mb-8">{t("social")}</h2></Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {social.slice(0, 6).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <a href={s.permalink || undefined} target={s.permalink ? "_blank" : undefined} rel="noreferrer"
                  className="group block overflow-hidden rounded-3xl bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="jeugd-social-card">
                  <div className="aspect-square overflow-hidden bg-[#F7F5F0]">{s.image && <img src={s.image} alt={s.handle} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />}</div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#E07A5F] mb-1">
                      {s.platform === "instagram" ? <Instagram size={15} /> : <Facebook size={15} />} {s.handle}
                      {s.date && <span className="text-xs font-normal text-[#52525B]/50">· {fmtDate(s.date, lang)}</span>}
                    </div>
                    <p className="text-sm text-[#52525B] line-clamp-3">{s.text}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
          {!social[0]?.live && <p className="mt-4 text-xs text-[#52525B]/60">* {t("social")} — placeholder. Automatische feed wordt gekoppeld zodra beschikbaar.</p>}
        </section>

        {/* TikTok */}
        <section data-testid="jeugd-tiktok-section">
          <Reveal><h2 className="font-fredoka text-4xl font-bold mb-8">TikTok</h2></Reveal>
          <Reveal>
            <div className="rounded-3xl bg-white p-6 md:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <TikTokEmbed />
              <div className="mt-6 text-center">
                <a href="https://www.tiktok.com/@inkad.diegem" target="_blank" rel="noreferrer" data-testid="jeugd-tiktok-follow"
                  className="inline-flex items-center gap-2 rounded-full bg-[#262626] px-6 py-3 text-sm font-semibold text-white transition-transform duration-150 hover:scale-105">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>
                  {t("followTiktok")}
                </a>
              </div>
            </div>
          </Reveal>
        </section>

        {/* Join CTA */}
        <Reveal>
          <section className="rounded-[2.5rem] bg-gradient-to-br from-[#E07A5F] to-[#d2664a] p-10 md:p-16 text-center text-white" data-testid="jeugd-join">
            <h2 className="font-fredoka text-4xl md:text-5xl font-bold">{t("howJoin")}</h2>
            <p className="mt-4 max-w-md mx-auto text-white/90">{t("jeugdDesc")}</p>
            <Link to="/jeugd/inschrijven" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-bold text-[#E07A5F] hover:scale-105 transition-transform duration-200">
              {t("joinJeugd")} <ArrowRight size={18} />
            </Link>
          </section>
        </Reveal>
      </div>
    </div>
  );
}
